import { useEffect, useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building2,
  Edit,
} from "lucide-react";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import API from "../api/api01";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PATHS } from "../config/paths";

export function ViewProfile({ user: initialUser }: any) {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, [initialUser.id]);

  const fetchProfile = async () => {
    try {
      const res = await API.get(`/superadmin/profile/${initialUser.id}`);
      setData(res.data);
    } catch {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const profile = data || initialUser;

  const initials = profile.name
    ?.split(" ")
    .map((n: string) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">

      {/* 🔝 HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">User Profile</h1>
          <p className="text-sm text-muted-foreground">
            Manage user information and details
          </p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline">Discard</Button>
          <Button onClick={() => navigate(PATHS.EDIT_PROFILE)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit Profile
          </Button>
        </div>
      </div>

      {/* 🧱 MAIN GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* LEFT SIDE */}
        <div className="lg:col-span-2 space-y-6">

          {/* PROFILE HEADER */}
          <div className="border rounded-xl p-5 flex items-center gap-4">
            <Avatar className="h-14 w-14">
              <AvatarImage
                src={`https://api.dicebear.com/7.x/initials/svg?seed=${profile.email}`}
              />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>

            <div>
              <h2 className="font-semibold text-base">{profile.name}</h2>
              <p className="text-sm text-muted-foreground">
                {profile.email}
              </p>

              <div className="flex gap-2 mt-2">
                <Badge variant="secondary">{profile.role}</Badge>
                <Badge variant="outline">{profile.rollNo}</Badge>
              </div>
            </div>
          </div>

          {/* DETAILS */}
          <div className="border rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Profile Details
            </h3>

            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Email" value={profile.email} icon={Mail} />
              <Field label="Phone" value={profile.phone || "N/A"} icon={Phone} />
              <Field label="Department" value={profile.department || "N/A"} icon={Building2} />
              <Field label="Join Date" value={profile.joinDate || "N/A"} icon={Calendar} />
            </div>

            <div>
              <Field
                label="Address"
                value={profile.address || "N/A"}
                icon={MapPin}
                full
              />
            </div>
          </div>

        </div>

        {/* RIGHT SIDE PANEL */}
        <div className="space-y-6">

          {/* STATUS CARD */}
          <div className="border rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Account Status
            </h3>

            <div className="flex items-center justify-between">
              <span className="text-sm">Status</span>
              <Badge className="bg-emerald-500/10 text-emerald-600">
                Active
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm">Access Level</span>
              <span className="text-sm font-medium">Full Access</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm">Last Login</span>
              <span className="text-sm font-medium">
                {new Date().toLocaleString()}
              </span>
            </div>
          </div>

          {/* QUICK INFO */}
          <div className="border rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Quick Info
            </h3>

            <div className="flex justify-between text-sm">
              <span>User ID</span>
              <span className="font-medium">{profile.id}</span>
            </div>

            <div className="flex justify-between text-sm">
              <span>Role</span>
              <span className="font-medium">{profile.role}</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

/* 🔹 FIELD COMPONENT */
function Field({ label, value, icon: Icon, full }: any) {
  return (
    <div className={`flex gap-3 ${full ? "w-full" : ""}`}>
      <Icon className="h-4 w-4 text-muted-foreground mt-1" />
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium">{value}</p>
      </div>
    </div>
  );
}